/**
 * About page data - Firebase-ready structure
 * Contains company information, history, values, team, and statistics
 */
import { Timestamp } from 'firebase/firestore';

// Helper to create mock timestamps
const createTimestamp = (): Timestamp => {
  return {
    seconds: Math.floor(Date.now() / 1000),
    nanoseconds: 0,
    toDate: () => new Date(),
    toMillis: () => Date.now(),
    isEqual: () => false,
    valueOf: () => '',
  } as unknown as Timestamp;
};

// ==========================================
// TYPE DEFINITIONS
// ==========================================

export interface CompanyOverview {
  name: string;
  tagline: string;
  mission: string;
  vision: string;
  story: string[];
  foundedYear: number;
  founderName: string;
}

export interface Stat {
  id: string;
  value: number;
  suffix: string;
  label: string;
  icon: string;
  description: string;
}

export interface TimelineEvent {
  id: string;
  year: number;
  title: string;
  description: string;
  icon: string;
  isHighlight: boolean;
}

export interface CompanyValue {
  id: string;
  title: string;
  description: string;
  icon: string;
  color: string;
}

export interface TeamMember {
  id: string;
  name: string;
  position: string;
  department: string;
  image: string;
  bio: string;
  linkedin?: string;
  email?: string;
  order: number;
}

export interface Certification {
  id: string;
  name: string;
  issuer: string;
  year: number;
  logo: string;
  description: string;
}

export interface Partner {
  id: string;
  name: string;
  logo: string;
  type: 'brand' | 'distributor' | 'technology';
  description: string;
  website?: string;
}

export interface WhyChooseUs {
  id: string;
  title: string;
  description: string;
  icon: string;
}

export interface AboutData {
  id: string;
  company: CompanyOverview;
  stats: Stat[];
  timeline: TimelineEvent[];
  values: CompanyValue[];
  team: TeamMember[];
  certifications: Certification[];
  partners: Partner[];
  whyChooseUs: WhyChooseUs[];
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// ==========================================
// MOCK DATA
// ==========================================

export const aboutData: AboutData = {
  id: 'sunvivo-about',
  company: {
    name: 'Sunvivo',
    tagline: 'Empowering Dental Excellence Since 2014',
    mission: 'To provide dental professionals with premium quality equipment and supplies at competitive prices, enabling them to deliver the best possible care to their patients.',
    vision: 'To be India\'s most trusted dental equipment company, known for quality products, exceptional service, and innovation in dental healthcare.',
    story: [
      'Sunvivo was founded in 2014 with a simple yet powerful vision: to bridge the gap between dental professionals and high-quality equipment. Our founder, with over 15 years of experience in the dental industry, recognized the challenges faced by practitioners in sourcing reliable equipment at fair prices.',
      'Starting from a small office in Gurgaon, we began by partnering with international manufacturers who shared our commitment to quality. Our first products were handpieces and sterilization equipment, chosen for their critical role in dental practice.',
      'Today, Sunvivo has grown to serve thousands of dental clinics, hospitals, and educational institutions across India. We\'ve expanded our catalog to include everything from basic consumables to advanced implant systems, always maintaining our founding principles of quality, reliability, and customer service.',
      'As we look to the future, we remain committed to innovation and excellence. We continue to seek out the latest technologies and products that can help dental professionals provide better care, faster recovery, and improved patient outcomes.',
    ],
    foundedYear: 2014,
    founderName: 'Dr. Rajesh Kumar',
  },
  stats: [
    {
      id: 'stat-years',
      value: 10,
      suffix: '+',
      label: 'Years of Excellence',
      icon: 'FiAward',
      description: 'Serving the dental industry since 2014',
    },
    {
      id: 'stat-customers',
      value: 5000,
      suffix: '+',
      label: 'Happy Customers',
      icon: 'FiUsers',
      description: 'Dental professionals trust us',
    },
    {
      id: 'stat-products',
      value: 500,
      suffix: '+',
      label: 'Products',
      icon: 'FiPackage',
      description: 'Comprehensive dental catalog',
    },
    {
      id: 'stat-cities',
      value: 200,
      suffix: '+',
      label: 'Cities Served',
      icon: 'FiMapPin',
      description: 'Pan-India delivery network',
    },
  ],
  timeline: [
    {
      id: 'tl-2014',
      year: 2014,
      title: 'Company Founded',
      description: 'Sunvivo was established in Gurgaon with a focus on handpieces and sterilization equipment.',
      icon: 'FiFlag',
      isHighlight: true,
    },
    {
      id: 'tl-2015',
      year: 2015,
      title: 'First Brand Partnership',
      description: 'Became authorized distributor for Hagen handpieces, expanding our quality offerings.',
      icon: 'FiAward',
      isHighlight: false,
    },
    {
      id: 'tl-2017',
      year: 2017,
      title: 'Warehouse Expansion',
      description: 'Opened a 10,000 sq ft warehouse in Manesar to serve growing demand.',
      icon: 'FiHome',
      isHighlight: true,
    },
    {
      id: 'tl-2018',
      year: 2018,
      title: 'Endodontic Range Launch',
      description: 'Introduced Dexell rotary files and endodontic instruments to our catalog.',
      icon: 'FiTool',
      isHighlight: false,
    },
    {
      id: 'tl-2020',
      year: 2020,
      title: 'Digital Transformation',
      description: 'Launched our e-commerce platform for seamless online ordering during pandemic.',
      icon: 'FiMonitor',
      isHighlight: true,
    },
    {
      id: 'tl-2022',
      year: 2022,
      title: 'Delhi Showroom',
      description: 'Opened our flagship showroom in Connaught Place for product demonstrations.',
      icon: 'FiMapPin',
      isHighlight: false,
    },
    {
      id: 'tl-2024',
      year: 2024,
      title: '5000+ Customers Milestone',
      description: 'Celebrated serving over 5000 dental professionals across India.',
      icon: 'FiUsers',
      isHighlight: true,
    },
  ],
  values: [
    {
      id: 'value-quality',
      title: 'Quality First',
      description: 'We never compromise on quality. Every product in our catalog is thoroughly vetted and sourced from certified manufacturers.',
      icon: 'FiAward',
      color: '#0077B6',
    },
    {
      id: 'value-integrity',
      title: 'Integrity',
      description: 'We believe in transparent business practices, honest pricing, and building trust with our customers and partners.',
      icon: 'FiShield',
      color: '#059669',
    },
    {
      id: 'value-customer',
      title: 'Customer Focus',
      description: 'Our customers are at the heart of everything we do. We listen, we learn, and we continuously improve our services.',
      icon: 'FiHeart',
      color: '#DC2626',
    },
    {
      id: 'value-innovation',
      title: 'Innovation',
      description: 'We stay ahead of industry trends, constantly updating our catalog with the latest dental technologies and solutions.',
      icon: 'FiZap',
      color: '#7C3AED',
    },
    {
      id: 'value-reliability',
      title: 'Reliability',
      description: 'Dental professionals depend on us for critical supplies. We ensure consistent quality and timely delivery, every time.',
      icon: 'FiCheck',
      color: '#0891B2',
    },
    {
      id: 'value-partnership',
      title: 'Partnership',
      description: 'We view every customer relationship as a partnership. Your success is our success, and we\'re committed to supporting your practice.',
      icon: 'FiUsers',
      color: '#D97706',
    },
  ],
  team: [
    {
      id: 'team-1',
      name: 'Dr. Rajesh Kumar',
      position: 'Founder & CEO',
      department: 'Leadership',
      image: '/images/team/rajesh-kumar.jpg',
      bio: 'With over 20 years in the dental industry, Dr. Kumar founded Sunvivo with a vision to make quality dental equipment accessible to all practitioners.',
      linkedin: 'https://linkedin.com/in/rajeshkumar',
      email: 'rajesh@sunvivo.in',
      order: 1,
    },
    {
      id: 'team-2',
      name: 'Priya Sharma',
      position: 'Head of Operations',
      department: 'Operations',
      image: '/images/team/priya-sharma.jpg',
      bio: 'Priya manages our supply chain and logistics, ensuring products reach customers quickly and efficiently.',
      linkedin: 'https://linkedin.com/in/priyasharma',
      email: 'priya@sunvivo.in',
      order: 2,
    },
    {
      id: 'team-3',
      name: 'Amit Verma',
      position: 'Sales Director',
      department: 'Sales',
      image: '/images/team/amit-verma.jpg',
      bio: 'Amit leads our sales team with 15+ years of experience in medical equipment sales across India.',
      linkedin: 'https://linkedin.com/in/amitverma',
      email: 'amit@sunvivo.in',
      order: 3,
    },
    {
      id: 'team-4',
      name: 'Dr. Sneha Patel',
      position: 'Technical Advisor',
      department: 'Technical',
      image: '/images/team/sneha-patel.jpg',
      bio: 'A practicing dentist, Dr. Sneha provides clinical insights and product recommendations based on real-world experience.',
      linkedin: 'https://linkedin.com/in/snehapatel',
      email: 'sneha@sunvivo.in',
      order: 4,
    },
  ],
  certifications: [
    {
      id: 'cert-iso',
      name: 'ISO 9001:2015',
      issuer: 'Bureau Veritas',
      year: 2018,
      logo: '/images/certifications/iso-9001.png',
      description: 'Quality Management System certification ensuring consistent quality in all our processes.',
    },
    {
      id: 'cert-iso-medical',
      name: 'ISO 13485:2016',
      issuer: 'TUV SUD',
      year: 2020,
      logo: '/images/certifications/iso-13485.png',
      description: 'Medical Devices Quality Management certification for handling medical equipment.',
    },
    {
      id: 'cert-fssai',
      name: 'FSSAI License',
      issuer: 'Food Safety and Standards Authority of India',
      year: 2019,
      logo: '/images/certifications/fssai.png',
      description: 'License for handling and distributing medical consumables.',
    },
  ],
  partners: [
    {
      id: 'partner-3star',
      name: '3Star',
      logo: '/images/brands/3star-logo.png',
      type: 'brand',
      description: 'Leading manufacturer of dental disposables and accessories.',
      website: 'https://3stardental.com',
    },
    {
      id: 'partner-hagen',
      name: 'Hagen',
      logo: '/images/brands/hagen-logo.png',
      type: 'brand',
      description: 'Premium handpieces with German precision engineering.',
      website: 'https://hagen-dental.com',
    },
    {
      id: 'partner-dexell',
      name: 'Dexell',
      logo: '/images/brands/dexell-logo.png',
      type: 'brand',
      description: 'German technology endodontic files and burs.',
      website: 'https://dexell.com',
    },
    {
      id: 'partner-nexen',
      name: 'Nexen',
      logo: '/images/brands/nexen-logo.png',
      type: 'brand',
      description: 'Next generation dental sterilization equipment.',
      website: 'https://nexen-dental.com',
    },
  ],
  whyChooseUs: [
    {
      id: 'why-quality',
      title: 'Certified Quality Products',
      description: 'All our products come from ISO certified manufacturers with proper quality documentation.',
      icon: 'FiCheckCircle',
    },
    {
      id: 'why-price',
      title: 'Competitive Pricing',
      description: 'Direct partnerships with manufacturers allow us to offer the best prices in the market.',
      icon: 'LuIndianRupee',
    },
    {
      id: 'why-shipping',
      title: 'Fast & Free Shipping',
      description: 'Free delivery on orders above ₹5,000 with quick turnaround times.',
      icon: 'FiTruck',
    },
    {
      id: 'why-support',
      title: 'Expert Support',
      description: 'Our team includes dental professionals who understand your needs.',
      icon: 'FiHeadphones',
    },
    {
      id: 'why-warranty',
      title: 'Warranty Coverage',
      description: 'Comprehensive warranty on all equipment with hassle-free claims.',
      icon: 'FiShield',
    },
    {
      id: 'why-selection',
      title: 'Wide Selection',
      description: 'From basic consumables to advanced equipment, find everything you need.',
      icon: 'FiGrid',
    },
  ],
  createdAt: createTimestamp(),
  updatedAt: createTimestamp(),
};

// Helper functions
export const getAboutData = (): AboutData => aboutData;

export const getCompanyStats = (): Stat[] => aboutData.stats;

export const getTimeline = (): TimelineEvent[] => {
  return [...aboutData.timeline].sort((a, b) => a.year - b.year);
};

export const getTimelineHighlights = (): TimelineEvent[] => {
  return aboutData.timeline.filter(event => event.isHighlight);
};

export const getCompanyValues = (): CompanyValue[] => aboutData.values;

export const getTeamMembers = (): TeamMember[] => {
  return [...aboutData.team].sort((a, b) => a.order - b.order);
};

export const getTeamByDepartment = (department: string): TeamMember[] => {
  return aboutData.team.filter(member => member.department === department);
};

export const getCertifications = (): Certification[] => aboutData.certifications;

export const getPartners = (): Partner[] => aboutData.partners;

export const getWhyChooseUs = (): WhyChooseUs[] => aboutData.whyChooseUs;
